/**
 * Minimal HTML sanitizer to reduce XSS risk when rendering user-authored templates.
 *
 * Notes:
 * - This is intentionally conservative (strips scripts/iframes/etc + inline event handlers).
 * - For full HTML sanitization with rich allowlists, consider a battle-tested library.
 */
export function sanitizeHtml(input: string): string {
  if (!input) return "";

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(input), "text/html");

    // Remove entire dangerous elements.
    const blockedSelectors = [
      "script",
      "iframe",
      "object",
      "embed",
      "link[rel='import']",
      "meta[http-equiv='refresh']",
    ];
    doc.querySelectorAll(blockedSelectors.join(",")).forEach((el) => el.remove());

    // Remove inline event handlers and javascript: URLs.
    doc.querySelectorAll("*").forEach((el) => {
      // Drop any on* attribute (onclick, onload, etc).
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        const value = (attr.value || "").trim();

        if (name.startsWith("on")) {
          el.removeAttribute(attr.name);
          continue;
        }

        if ((name === "href" || name === "src") && /^javascript:/i.test(value)) {
          el.removeAttribute(attr.name);
          continue;
        }

        // Prevent CSS-based exfil / JS execution vectors.
        if (name === "style" && /expression\s*\(|url\s*\(\s*javascript:/i.test(value)) {
          el.removeAttribute(attr.name);
          continue;
        }
      }
    });

    return doc.body?.innerHTML ?? "";
  } catch {
    // If parsing fails, don't render arbitrary raw HTML.
    return "";
  }
}

